import { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  ScatterController,
  Filler,
  CategoryScale,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
  ScatterController,
  Filler
);

export default function IntensityPlotAudio({ intensity, time_stamped, currentTime = 0 }) {
  const [isMobile, setIsMobile] = useState(false);
  const [showWordLabels, setShowWordLabels] = useState(true);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setShowWordLabels(!mobile && words.length <= 12);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [time_stamped?.words?.length]);

  const xs = intensity?.intensity?.xs || [];
  const ys = intensity?.intensity?.ys || [];

  if (!xs.length) return null;

  const yMin = Math.min(...ys);
  const yMax = Math.max(...ys);
  const yRange = yMax - yMin;

  const datasets = [];
  const words = time_stamped?.words || [];

  // 🔵 Main Intensity Line - optimized for mobile
  datasets.push({
    label: "Intensity (dB)",
    data: xs.map((x, i) => ({ x, y: ys[i] })),
    borderColor: "#2563eb",
    borderWidth: isMobile ? 1.5 : 2,
    tension: isMobile ? 0.2 : 0.4,
    pointRadius: 0,
    datalabels: { display: false },
  });

  // 🟠 Word vertical lines + labels - thinner on mobile
  words.forEach((w) => {
    const extend = yRange * 0.1;

    datasets.push({
      label: `start-${w.text}`,
      data: [
        { x: w.start, y: yMin - extend },
        { x: w.start, y: yMax },
      ],
      borderColor: "rgba(200,0,0,0.3)",
      borderWidth: isMobile ? 0.5 : 1,
      pointRadius: 0,
      showLine: true,
      datalabels: { display: false },
    });

    datasets.push({
      label: `end-${w.text}`,
      data: [
        { x: w.end, y: yMin - extend },
        { x: w.end, y: yMax },
      ],
      borderColor: "rgba(0,0,0,0.2)",
      borderWidth: isMobile ? 0.5 : 1,
      pointRadius: 0,
      showLine: true,
      datalabels: { display: false },
    });
  });

  // 🏷 Word labels under axis - conditional display
  const shouldShowWordLabels = showWordLabels && words.length > 0 && words.length <= 12;
  
  if (shouldShowWordLabels) {
    datasets.push({
      label: "Word labels",
      type: "scatter",
      data: words.map((w) => ({
        x: (w.start + w.end) / 2,
        y: yMin,
        text: w.text,
        intervalWidth: w.end - w.start,
      })),
      pointRadius: 0,
      datalabels: {
        display: true,
        color: "#111",
        font: { 
          weight: "bold", 
          size: isMobile ? 10 : 13 
        },
        formatter: (v) => v.text,
        align: "bottom",
        anchor: "end",
        clip: false,
        rotation: (ctx) =>
          ctx.dataset.data[ctx.dataIndex].intervalWidth < 0.05 ? 45 : 0,
        yAdjust: isMobile ? 4 : 6,
      },
    });
  }

  // 🟢 Moving cursor (current playback time)
  const cursorLine = [
    { x: currentTime, y: yMin - yRange * 0.1 },
    { x: currentTime, y: yMax + yRange * 0.1 },
  ];
  datasets.push({
    label: "Playback cursor",
    data: cursorLine,
    borderColor: "rgba(16,185,129,1)",
    borderWidth: isMobile ? 1.5 : 2,
    pointRadius: 0,
    showLine: true,
    datalabels: { display: false },
  });

  const data = { datasets };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    scales: {
      x: {
        type: "linear",
        grid: { display: true, color: "rgba(0,0,0,0.05)" },
        ticks: { display: false },
        title: { 
          display: !isMobile, 
          text: "Time (seconds)",
          font: { size: isMobile ? 12 : 14 }
        }
      },
      y: {
        min: yMin - yRange * 0.15,
        max: yMax + yRange * 0.1,
        grid: { color: "rgba(0,0,0,0.05)" },
        title: { 
          display: true, 
          text: "Intensity (dB)",
          font: { size: isMobile ? 12 : 14 }
        },
        ticks: {
          font: { size: isMobile ? 10 : 12 }
        }
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: !isMobile,
        mode: "nearest", 
        intersect: true 
      },
      datalabels: { display: false },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  };

  return (
    <div className="flex flex-col gap-2 w-full min-w-0">
      {/* Word labels toggle for mobile */}
      {isMobile && words.length > 0 && (
        <label className="flex items-center gap-2 select-none text-xs">
          <input
            type="checkbox"
            checked={showWordLabels}
            onChange={() => setShowWordLabels(!showWordLabels)}
            className="scale-90"
          />
          Show word labels
        </label>
      )}

      {/* 📉 Chart wrapper: responsive and mobile optimized */}
      <div className={`w-full min-w-0 overflow-x-auto bg-white rounded-lg border border-gray-200 p-2 ${
        isMobile ? 'h-48' : 'h-64'
      }`}>
        <Line data={data} options={options} />
      </div>

      {/* Mobile info text */}
      {isMobile && (
        <p className="text-xs text-gray-500 text-center">
          Pinch to zoom • Word labels: {showWordLabels ? 'ON' : 'OFF'}
        </p>
      )}
    </div>
  );
}
